'use strict';

/**
 * T323 P1-b — hybrid-envelope: the single both-halves-or-fail envelope layer.
 *
 * DOCTRINE (LD-18, LD-3): every hybrid-PQ consumer routes through this module.
 * No consumer calls raw `hybrid-kex.js` decapsulation directly. The envelope
 * wraps BOTH the X25519 half and the ML-KEM half (via hybrid-kex, which HKDF-
 * combines the two shared secrets) plus the AES-256-GCM ciphertext, in a single
 * versioned, algo-tagged structure. Decryption FAILS CLOSED with a structured
 * `DowngradeError` (code `ERR_DOWNGRADE_DETECTED`, NOT a TypeError, NEVER
 * plaintext) if either KEX half is absent or malformed.
 *
 * Envelope shape:
 *   {
 *     algo: 'v2.x25519+mlkem768.aes256gcm',  // algo-versions MAIL_HYBRID_V2 (T513);
 *                                             // pre-T513 envelopes carry MAIL_HYBRID_V1
 *                                             // and are READ under the retired v1 combiner
 *     kemCiphertext: '<hex>',                 // ML-KEM-768 ciphertext (PQ half)
 *     x25519EphemeralPublic: '<hex>',         // X25519 ephemeral pub (classical half)
 *     iv: '<hex>',
 *     ct: '<hex>',
 *     tag: '<hex>',
 *   }
 *
 * The downgrade guard is BEFORE any decapsulation: stripping `kemCiphertext`
 * (PQ half) OR `x25519EphemeralPublic` (classical half) throws DowngradeError.
 */

const crypto = require('node:crypto');
const hybridKex = require('./hybrid-kex.js');
const { MAIL_HYBRID_V2, MAIL_HYBRID_ALGOS, KEX_V1, kexVersionForAlgo } = require('./algo-versions.js');

const ALGO = 'aes-256-gcm';
// T513 — new writes are sealed under the transcript-bound v2 combiner and
// tagged so. Reads route the combiner version on the tag (see unwrapEnvelope).
const ENVELOPE_ALGO = MAIL_HYBRID_V2; // 'v2.x25519+mlkem768.aes256gcm'
// Domain-separation label binding the HKDF output to envelope use.
const ENVELOPE_LABEL = 'oyster:hybrid-envelope:v1';

class DowngradeError extends Error {
  constructor(message, details) {
    super(message || 'hybrid envelope downgrade detected');
    this.name = 'DowngradeError';
    this.code = 'ERR_DOWNGRADE_DETECTED';
    if (details && typeof details === 'object') Object.assign(this, details);
  }
}

const ERR_DOWNGRADE_DETECTED = 'ERR_DOWNGRADE_DETECTED';

function _toPlainBuf(plaintext) {
  if (typeof plaintext === 'string') return Buffer.from(plaintext, 'utf8');
  if (Buffer.isBuffer(plaintext)) return plaintext;
  if (plaintext instanceof Uint8Array) return Buffer.from(plaintext);
  throw new TypeError('hybrid-envelope: plaintext must be string/Buffer/Uint8Array');
}

// Derive the AES-256 key from the hybrid shared secret, bound to envelope use.
function _kekFromSharedSecret(sharedSecret) {
  return crypto.createHash('sha256')
    .update(ENVELOPE_LABEL)
    .update(Buffer.from(sharedSecret))
    .digest();
}

/**
 * Wrap plaintext into a both-halves hybrid envelope.
 *
 * @param {string|Buffer|Uint8Array} plaintext
 * @param {{x25519: Uint8Array, mlKem: Uint8Array}} recipientPublicKey
 * @returns {object} versioned hybrid envelope
 */
function wrapEnvelope(plaintext, recipientPublicKey) {
  const plainBuf = _toPlainBuf(plaintext);
  // encapsulate validates recipientPublicKey shape (both halves required).
  const { kemCiphertext, x25519EphemeralPublic, sharedSecret } = hybridKex.encapsulate(recipientPublicKey);
  const key = _kekFromSharedSecret(sharedSecret);
  const iv = crypto.randomBytes(12);
  const cipher = crypto.createCipheriv(ALGO, key, iv);
  const ct = Buffer.concat([cipher.update(plainBuf), cipher.final()]);
  const tag = cipher.getAuthTag();
  key.fill(0);
  return {
    algo: ENVELOPE_ALGO,
    kemCiphertext: Buffer.from(kemCiphertext).toString('hex'),
    x25519EphemeralPublic: Buffer.from(x25519EphemeralPublic).toString('hex'),
    iv: iv.toString('hex'),
    ct: ct.toString('hex'),
    tag: tag.toString('hex'),
  };
}

/**
 * Unwrap a both-halves hybrid envelope. Throws DowngradeError
 * (code ERR_DOWNGRADE_DETECTED) if EITHER KEX half is absent or malformed —
 * the no-downgrade invariant. Never returns plaintext from a stripped envelope.
 *
 * @param {object} envelope
 * @param {{x25519: Uint8Array, mlKem: Uint8Array}} recipientPrivateKey
 * @returns {Buffer} plaintext
 */
function unwrapEnvelope(envelope, recipientPrivateKey) {
  if (!envelope || typeof envelope !== 'object') {
    throw new DowngradeError('hybrid envelope missing or not an object');
  }
  // ── DOWNGRADE GUARD (before any decapsulation) ──
  // Both KEX halves MUST be present and well-formed. Absence of either half is
  // a downgrade attack — fail closed with the structured error.
  const hasKem = typeof envelope.kemCiphertext === 'string' && envelope.kemCiphertext.length > 0;
  const hasX25519 = typeof envelope.x25519EphemeralPublic === 'string' && envelope.x25519EphemeralPublic.length > 0;
  if (!hasKem && !hasX25519) {
    throw new DowngradeError('both hybrid KEX halves are absent');
  }
  if (!hasKem) {
    throw new DowngradeError('ML-KEM (post-quantum) half stripped from hybrid envelope', { strippedHalf: 'mlkem768' });
  }
  if (!hasX25519) {
    throw new DowngradeError('X25519 (classical) half stripped from hybrid envelope', { strippedHalf: 'x25519' });
  }

  let kemCiphertext;
  let eph;
  try {
    kemCiphertext = Uint8Array.from(Buffer.from(envelope.kemCiphertext, 'hex'));
    eph = Uint8Array.from(Buffer.from(envelope.x25519EphemeralPublic, 'hex'));
  } catch (_e) {
    throw new DowngradeError('malformed hybrid KEX half (not hex)');
  }
  // Length checks against the hybrid-kex contract — a truncated/forged half is
  // a downgrade, not a decrypt error.
  if (kemCiphertext.length !== hybridKex.KEY_SIZES.kemCiphertext) {
    throw new DowngradeError('ML-KEM ciphertext wrong length', { strippedHalf: 'mlkem768' });
  }
  if (eph.length !== hybridKex.KEY_SIZES.x25519PublicKey) {
    throw new DowngradeError('X25519 ephemeral public wrong length', { strippedHalf: 'x25519' });
  }

  // T513 — route the KEX combiner version on the algo tag, BEFORE decapsulation
  // like the half-stripping guard above. A pre-T513 envelope is tagged
  // MAIL_HYBRID_V1 (or, from the oldest writers, untagged): both mean the
  // retired v1 combiner. A tag outside the MAIL hybrid family is a
  // downgrade/forgery and is refused here rather than decrypted under a guess.
  let kexVersion;
  if (envelope.algo === undefined) {
    kexVersion = KEX_V1;
  } else if (MAIL_HYBRID_ALGOS.includes(envelope.algo)) {
    kexVersion = kexVersionForAlgo(envelope.algo);
  } else {
    throw new DowngradeError('unsupported hybrid envelope algo tag', { algo: String(envelope.algo) });
  }
  const sharedSecret = hybridKex.decapsulate(recipientPrivateKey, kemCiphertext, eph, kexVersion);
  const key = _kekFromSharedSecret(sharedSecret);
  const decipher = crypto.createDecipheriv(ALGO, key, Buffer.from(envelope.iv, 'hex'));
  decipher.setAuthTag(Buffer.from(envelope.tag, 'hex'));
  let plain;
  try {
    plain = Buffer.concat([decipher.update(Buffer.from(envelope.ct, 'hex')), decipher.final()]);
  } finally {
    key.fill(0);
  }
  return plain;
}

module.exports = {
  wrapEnvelope,
  unwrapEnvelope,
  DowngradeError,
  ERR_DOWNGRADE_DETECTED,
  ENVELOPE_ALGO,
};
